import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Sparkles, X, Send, Loader2, Bot, Mic, Square } from 'lucide-react';
import { sendAssistantMessage, sendAssistantVoice } from '../../redux/assistantSlice';

// Floating AI Assistant (Phase 1: text chat MVP).
// Bottom-right button → chat drawer. Patients + doctors only; identity comes
// from the JWT on the backend. Mic button is a Phase-2 placeholder.
export default function FloatingAssistant() {
    const { t } = useTranslation(['assistant']);
    const dispatch = useDispatch();
    const role = typeof window !== 'undefined' ? localStorage.getItem('Role') : null;

    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [messages, setMessages] = useState([]); // { from: 'user'|'bot', text, options? }
    const sessionIdRef = useRef(`s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [messages, open]);

    // Phase 1 scope: doctor + patient only.
    if (role !== 'doctor' && role !== 'patient') return null;

    const send = async (textOverride) => {
        const text = (textOverride ?? input).trim();
        if (!text || busy) return;
        setInput('');
        setMessages((m) => [...m, { from: 'user', text }]);
        setBusy(true);
        try {
            const res = await dispatch(sendAssistantMessage({ message: text, sessionId: sessionIdRef.current })).unwrap();
            setMessages((m) => [...m, { from: 'bot', text: res?.reply || '…', options: Array.isArray(res?.options) ? res.options : [] }]);
        } catch (e) {
            setMessages((m) => [...m, { from: 'bot', text: t('assistant.error', 'Sorry — I could not answer right now. Please try again.') }]);
        } finally {
            setBusy(false);
        }
    };

    // ---- Voice input: record mic → Whisper STT → auto-send the transcript ----
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);

    const toggleMic = async () => {
        if (recording) {
            try { recorderRef.current?.stop(); } catch (e) { /* noop */ }
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream, MediaRecorder.isTypeSupported('audio/webm') ? { mimeType: 'audio/webm' } : undefined);
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach((tr) => tr.stop());
                setRecording(false);
                const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
                if (blob.size < 1000) return; // too short — ignore
                setTranscribing(true);
                try {
                    const res = await dispatch(sendAssistantVoice({ blob })).unwrap();
                    const text = (res?.text || '').trim();
                    if (text) await send(text); // auto-send the spoken message
                } catch (e) {
                    setMessages((m) => [...m, { from: 'bot', text: t('assistant.voice_error', 'Sorry — I could not understand the audio. Please try again.') }]);
                } finally {
                    setTranscribing(false);
                }
            };
            recorderRef.current = rec;
            rec.start();
            setRecording(true);
        } catch (e) {
            setMessages((m) => [...m, { from: 'bot', text: t('assistant.mic_denied', 'Microphone access was denied — please allow it in the browser.') }]);
        }
    };

    return (
        <>
            {/* Floating button */}
            {!open && (
                <button
                    type="button"
                    data-testid="assistant-fab"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-5 right-5 z-[900] w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] hover:scale-105 transition-transform"
                    title={t('assistant.open', 'AI Assistant')}
                >
                    <Sparkles className="w-6 h-6" />
                </button>
            )}

            {/* Chat drawer */}
            {open && (
                <div
                    data-testid="assistant-drawer"
                    className="fixed bottom-5 right-5 z-[900] w-[370px] max-w-[92vw] h-[520px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white">
                        <div className="flex items-center gap-2">
                            <Bot className="w-5 h-5" />
                            <span className="font-bold text-sm">{t('assistant.title', 'Therapeu AI Assistant')}</span>
                        </div>
                        <button type="button" data-testid="assistant-close" onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-white/15">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2 bg-gray-50/60" data-testid="assistant-messages">
                        {messages.length === 0 && (
                            <div className="text-center text-gray-400 text-sm mt-10 px-4">
                                <Sparkles className="w-7 h-7 mx-auto mb-2 text-[#4ABDBD]/60" />
                                {t('assistant.hint', 'Hi! Ask me anything about your appointments. Soon I will also be able to book, cancel and reschedule them for you.')}
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex flex-col ${m.from === 'user' ? 'items-end' : 'items-start'}`}>
                                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-line ${
                                    m.from === 'user'
                                        ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-br-sm'
                                        : 'bg-white border border-gray-200 text-gray-700 rounded-bl-sm'
                                }`}>
                                    {m.text}
                                </div>
                                {/* Tappable choice chips — only on the latest bot message */}
                                {m.from === 'bot' && i === messages.length - 1 && !busy && m.options?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-1.5 max-w-[85%]" data-testid="assistant-options">
                                        {m.options.map((opt) => (
                                            <button
                                                key={opt}
                                                type="button"
                                                onClick={() => send(opt)}
                                                className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#4ABDBD] text-[#2D8A8A] bg-white hover:bg-[#4ABDBD]/10 transition-colors"
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {busy && (
                            <div className="flex justify-start">
                                <div className="px-3 py-2 rounded-2xl bg-white border border-gray-200">
                                    <Loader2 className="w-4 h-4 animate-spin text-[#3AA8A8]" />
                                </div>
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    {/* Input */}
                    <div className="p-3 border-t border-gray-100 bg-white flex items-center gap-2">
                        {/* Mic — record, transcribe (Whisper via OpenRouter), auto-send */}
                        <button
                            type="button"
                            data-testid="assistant-mic"
                            onClick={toggleMic}
                            disabled={busy || transcribing}
                            title={recording ? t('assistant.stop_recording', 'Stop recording') : t('assistant.speak', 'Speak your message')}
                            className={`p-2 rounded-full transition-colors ${
                                recording
                                    ? 'bg-red-500 text-white animate-pulse'
                                    : transcribing
                                        ? 'text-gray-300 cursor-wait'
                                        : 'text-[#3AA8A8] hover:bg-[#4ABDBD]/10'
                            }`}
                        >
                            {recording ? <Square className="w-5 h-5" /> : transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
                        </button>
                        <input
                            data-testid="assistant-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                            placeholder={t('assistant.placeholder', 'Type a message…')}
                            className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40"
                        />
                        <button
                            type="button"
                            data-testid="assistant-send"
                            onClick={() => send()}
                            disabled={busy || !input.trim()}
                            className="p-2 rounded-full text-white bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] disabled:opacity-40"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
